import React, { useState, useEffect, useMemo, useRef } from 'react';
import { stripAcutes, isVowel, reflowStress, toggleAcuteAt } from '../../utils/stress';
import { backdropClose } from '../backdrop';

// Editor for the per-quest stress glossary (quest_data.voiceover_glossary) —
// the rule every voiceover text is read by: a place with no stress mark of
// its own gets the glossary's, when the text is voiced or shown in the audio
// studio. Nothing is written into the texts. Whole-list GET/PUT: the table is small,
// and server-side validation guards every entry on save.
//
// A stressed spelling is picked by CLICKING a vowel of the form — the acute
// (U+0301) goes right after the clicked letter. No one should have to type a
// combining character by hand.

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Same fields the server reads through the glossary — "still in the texts?" is checked against
// exactly what the voiceover reads.
const VOICED_FIELDS = ['description', 'after_message'];

const STATUS_BADGE = {
  auto: { text: 'auto', color: '#5f6368', hint: 'written by the collection batch, not reviewed yet' },
  edited: { text: '✎ edited', color: '#188038', hint: 'saved by a human — the pipeline never overwrites it' },
};

const GlossaryEditor = ({ questId, locations, onClose, onSaved, initialFilter }) => {
  const [rows, setRows] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState(initialFilter || '');   // e.g. the form a review row points at
  // Hide the «∅ no mark» rows (recorded negative decisions) — they matter to
  // the pipeline but not to a human scanning the marks. Remembered per user.
  const [hideNoMark, setHideNoMark] = useState(() => {
    try { return localStorage.getItem('glossaryHideNoMark') === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('glossaryHideNoMark', hideNoMark ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [hideNoMark]);
  const [dirty, setDirty] = useState(false);
  // Snapshot of every entry as loaded/saved, keyed by lowercased form. Save
  // sends only the DIFF against this snapshot (changed/new rows + deletions),
  // so entries appended meanwhile by a collection batch are never clobbered.
  const baseRef = useRef(new Map());

  const rowKey = (r) => stripAcutes(r.form || '').trim().toLowerCase();
  const rowJson = (r) => JSON.stringify({ form: r.form, stressed: r.stressed || null, status: r.status, note: r.note || '' });

  const adoptList = (list) => {
    const withKeys = (list || []).map((e, i) => ({ ...e, _key: `g${i}` }));
    baseRef.current = new Map(withKeys.map((r) => [rowKey(r), rowJson(r)]));
    setRows(withKeys);
  };

  useEffect(() => {
    let dead = false;
    fetch(`/api/pipeline/quest/${questId}/glossary`, { credentials: 'include' })
      .then((res) => res.json().then((data) => ({ res, data })))
      .then(({ res, data }) => {
        if (dead) return;
        if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
        adoptList(data.glossary);
      })
      .catch((e) => { if (!dead) setLoadError(e.message); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId]);

  // The live voiced texts, for the "not in texts" marker (a form left behind
  // by a prose edit — kept, never auto-deleted, just flagged).
  const corpus = useMemo(() => (locations || [])
    .map((l) => VOICED_FIELDS.map((f) => l[f] || '').join('\n'))
    .join('\n'), [locations]);
  // How many times each form occurs in the voiced texts (whole-word,
  // case-insensitive — the same matches the server's rule would make).
  // 0 = the form was left behind by a prose edit: kept, just flagged.
  const useCounts = useMemo(() => {
    const map = {};
    (rows || []).forEach((r) => {
      const form = stripAcutes(r.form || '').trim();
      if (!form) return;
      try {
        // Same boundary rule as the server's: letters, the acute and the
        // hyphen are not boundaries.
        const re = new RegExp(`(?<![\\p{L}\\u0301-])${escapeRegExp(form)}(?![\\p{L}\\u0301-])`, 'giu');
        map[r._key] = (corpus.match(re) || []).length;
      } catch (e) { map[r._key] = null; }
    });
    return map;
  }, [rows, corpus]);

  // Put a mark on the clicked vowel (see utils/stress).
  const toggleAcute = (r, form, i) => patchRow(r._key, { stressed: toggleAcuteAt(r.stressed, form, i) });

  const patchRow = (key, patch) => {
    setDirty(true);
    setSaveError(null);
    // Any human touch promotes the row out of 'auto' — the merge must keep it.
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch, status: 'edited' } : r)));
  };
  const removeRow = (key) => {
    setDirty(true);
    setSaveError(null);
    setRows((rs) => rs.filter((r) => r._key !== key));
  };
  const addRow = () => {
    setDirty(true);
    setRows((rs) => [{ form: '', stressed: null, status: 'edited', note: '', _key: `n${Date.now()}` }, ...rs]);
  };

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      // Diff vs the loaded snapshot: changed/new rows go as upserts, forms
      // that were loaded but are gone now go as deletes.
      const liveKeys = new Set(rows.map(rowKey).filter(Boolean));
      const upserts = rows
        .filter((r) => rowKey(r) && baseRef.current.get(rowKey(r)) !== rowJson(r))
        .map(({ _key, ...e }) => e);
      const deletes = [...baseRef.current.keys()].filter((k) => !liveKeys.has(k));
      const res = await fetch(`/api/pipeline/quest/${questId}/glossary`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ upserts, deletes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
      adoptList(data.glossary);
      setDirty(false);
      if (onSaved) onSaved(data.glossary || []);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Fresh unsaved rows (n-keys) always stay visible — a just-added word has
  // no mark yet and must not vanish under the no-mark filter.
  const noMarkCount = (rows || []).filter((r) => !r.stressed && !r._key.startsWith('n')).length;
  const shown = (rows || [])
    .filter((r) => !hideNoMark || r.stressed || r._key.startsWith('n'))
    .filter((r) => !filter.trim()
      || (r.form || '').toLowerCase().includes(filter.trim().toLowerCase())
      || (r.note || '').toLowerCase().includes(filter.trim().toLowerCase()));

  const cellBtn = {
    border: 'none', background: 'transparent', cursor: 'pointer', padding: '1px 0',
    fontSize: 15, fontFamily: 'inherit', lineHeight: 1.2,
  };

  // The word as clickable letters: a click puts the mark on that vowel and
  // takes it off again. A row being typed gets the strip too, so a new word
  // can be stressed on the spot instead of after a save.
  const vowelStrip = (r, form) => (
    <span>
      {form.split('').map((ch, i) => (
        <button
          key={i}
          style={{ ...cellBtn, cursor: isVowel(ch) ? 'pointer' : 'default', textDecoration: isVowel(ch) ? 'underline dotted' : 'none' }}
          disabled={!isVowel(ch)}
          title={isVowel(ch) ? 'Toggle a stress mark on this letter' : undefined}
          onClick={() => toggleAcute(r, form, i)}
        >{ch}</button>
      ))}
    </span>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} {...backdropClose(onClose)}>
      <div
        style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 760, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>📖 Stress glossary</h3>
          <span style={{ fontSize: 12, color: '#5f6368' }}>{rows ? `${rows.length} entrie(s)` : ''}</span>
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={hideNoMark} onChange={(e) => setHideNoMark(e.target.checked)} />
            Hide ∅ no-mark{noMarkCount ? ` (${noMarkCount})` : ''}
          </label>
          <input
            type="text" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', width: 160 }}
          />
          <button onClick={addRow} disabled={!rows} style={{ padding: '4px 10px', borderRadius: 4 }}>+ Add word</button>
        </div>
        <div style={{ fontSize: 11, color: '#5f6368', marginBottom: 8 }}>
          Click a vowel to toggle a stress mark there — hyphenated names may carry one per
          part. «∅» clears every mark (the word stays recorded as «checked, no mark
          needed»). Entries you save become «✎ edited» and are never overwritten by the
          collection batch.
        </div>

        {loadError && <div style={{ color: '#c5221f', fontSize: 12, marginBottom: 8 }}>✗ {loadError}</div>}
        {!rows && !loadError && <div style={{ fontSize: 12, color: '#5f6368' }}>⏳ Loading…</div>}

        {rows && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: 'var(--bg,#fff)', zIndex: 1 }}>
                  {['Form (click a vowel)', 'Uses', 'Stressed', 'Status', 'Note', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border,#ddd)', fontSize: 11, color: '#5f6368' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const form = stripAcutes(r.form || '');
                  return (
                    <tr key={r._key} style={{ borderBottom: '1px solid var(--border,#f2f2f2)' }}>
                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                        {r._key.startsWith('n') && (
                          <input
                            type="text" value={r.form || ''} placeholder="word form" autoFocus
                            onChange={(e) => patchRow(r._key, {
                              form: e.target.value,
                              stressed: reflowStress(r.stressed, e.target.value),
                            })}
                            style={{ fontSize: 13, padding: '2px 6px', width: 150, marginRight: 8 }}
                          />
                        )}
                        {form ? vowelStrip(r, form) : null}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {useCounts[r._key] == null ? '' : useCounts[r._key] === 0
                          ? <span title="This form no longer occurs in the voiced texts — kept, but currently matches nothing" style={{ color: '#9a6700' }}>0 ⚠</span>
                          : <span style={{ color: '#5f6368' }}>×{useCounts[r._key]}</span>}
                      </td>
                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                        {r.stressed
                          ? <b>{r.stressed}</b>
                          : <span style={{ color: '#5f6368' }} title="No mark will be placed for this word">∅ no mark</span>}
                        {r.stressed && (
                          <button
                            onClick={() => patchRow(r._key, { stressed: null })}
                            title="Clear every mark (keep the word as checked, no mark needed)"
                            style={{ ...cellBtn, marginLeft: 6, fontSize: 12, color: '#5f6368' }}
                          >∅</button>
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                        {(() => { const b = STATUS_BADGE[r.status] || STATUS_BADGE.auto; return (
                          <span title={b.hint} style={{ fontSize: 11, color: b.color }}>{b.text}</span>
                        ); })()}
                      </td>
                      <td style={{ padding: '4px 8px', width: '40%' }}>
                        <input
                          type="text" value={r.note || ''} placeholder=""
                          onChange={(e) => patchRow(r._key, { note: e.target.value })}
                          style={{ fontSize: 12, padding: '2px 6px', width: '100%', boxSizing: 'border-box' }}
                        />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <button
                          onClick={() => removeRow(r._key)}
                          title="Delete this entry"
                          aria-label={`Delete ${r.form}`}
                          style={{ ...cellBtn, color: '#c5221f', fontSize: 13 }}
                        >✕</button>
                      </td>
                    </tr>
                  );
                })}
                {!shown.length && (
                  <tr><td colSpan={6} style={{ padding: 12, fontSize: 12, color: '#5f6368' }}>
                    {rows.length ? 'Nothing matches the filter.' : 'Empty — run «Collect glossary» or add words by hand.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {saveError && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 8 }}>✗ {saveError}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 11, color: '#9a6700', marginRight: 'auto' }}>Unsaved changes</span>}
          <button onClick={onClose}>Close</button>
          <button
            onClick={save}
            disabled={!rows || busy || !dirty}
            style={{ background: '#8e44ad', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
          >{busy ? 'Saving…' : 'Save glossary'}</button>
        </div>
      </div>
    </div>
  );
};

export default GlossaryEditor;
